import React, { useState, Suspense, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { NavTray } from './NavTray';
import { BottomTabBar } from './BottomTabBar';
import { Loader } from '@/components/ui/Loader';
import { Icon } from '@/components/ui/Icon';
import { useSession } from '@/lib/session';
import { useApi } from '@/lib/hooks';
import { RouteErrorBoundary } from './ErrorBoundary';
import { useAdminStore } from '@/admin/adminStore';
import { RouteGuard } from '@/admin/RouteGuard';
import styles from './Shell.module.css';

export function Shell() {
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const { role } = useSession();
  const adminStore = useAdminStore();

  const handleToggleMobileNav = React.useCallback(() => {
    setIsMobileNavOpen((prev) => !prev);
  }, []);

  const handleCloseMobileNav = React.useCallback(() => {
    setIsMobileNavOpen(false);
  }, []);

  // Fetch notices once without polling
  const { data: noticesData } = useApi('/notices');
  const notices = Array.isArray(noticesData) ? noticesData : noticesData?.data || [];

  const [dismissedId, setDismissedId] = useState(() => {
    try {
      return sessionStorage.getItem('emergency_dismissed_id');
    } catch {
      return null;
    }
  });

  const urgentNotice = notices.find(
    (n) =>
      (n.pinned === 1 && (n.title.startsWith('[EMERGENCY]') || n.category === 'urgent')) ||
      n.title.startsWith('[EMERGENCY]')
  );

  const showBanner = urgentNotice && String(urgentNotice.id) !== String(dismissedId);

  const handleDismiss = (id) => {
    try {
      sessionStorage.setItem('emergency_dismissed_id', String(id));
    } catch {
      // ignore
    }
    setDismissedId(String(id));
  };

  const location = useLocation();
  const effectiveRole = role || (location.pathname.startsWith('/student') ? 'student' : location.pathname.startsWith('/parent') ? 'parent' : null);
  const hasBottomBar = effectiveRole === 'student' || effectiveRole === 'parent';

  return (
    <div className={styles.appLayout}>
      {/* Desktop Sidebar (>=1024px) */}
      <div className={styles.desktopSidebar}>
        <Sidebar />
      </div>

      {/* Main Content Column */}
      <div className={`${styles.mainColumn} ${hasBottomBar ? styles.withBottomBar : ''}`}>
        <Topbar
          isMobileNavOpen={isMobileNavOpen}
          onToggleMobileNav={handleToggleMobileNav}
        />

        <NavTray
          isOpen={isMobileNavOpen}
          onClose={handleCloseMobileNav}
        />

        {/* Emergency Announcement Banner (across all roles) */}
        {showBanner && (
          <aside className={styles.emergencyBanner} role="alert" aria-live="assertive">
            <div className={styles.emergencyContent}>
              <span className={styles.emergencyBadge}>
                <span className={styles.pulseDot} />
                EMERGENCY NOTICE
              </span>
              <div className={styles.emergencyText}>
                <strong>{urgentNotice.title.replace(/^\[EMERGENCY\]\s*/i, '')}</strong>
                <span className={styles.emergencySeparator}>—</span>
                <span>{urgentNotice.body}</span>
              </div>
            </div>
            <button
              type="button"
              className={styles.dismissEmergencyBtn}
              onClick={() => handleDismiss(urgentNotice.id)}
              aria-label="Dismiss emergency notice"
            >
              <Icon name="x" size={16} />
            </button>
          </aside>
        )}

        {/* Global Announcement Banner from Admin Console */}
        {adminStore.bannerActive && adminStore.bannerText && (
          <aside className={styles.emergencyBanner} style={{ backgroundColor: 'var(--color-primary-subtle, #fff7ed)', borderColor: 'var(--color-primary, #f97316)' }} role="alert">
            <div className={styles.emergencyContent}>
              <span className={styles.emergencyBadge} style={{ backgroundColor: 'var(--color-primary, #f97316)', color: '#ffffff' }}>
                ANNOUNCEMENT
              </span>
              <div className={styles.emergencyText} style={{ color: 'var(--color-text, #0f172a)' }}>
                <span>{adminStore.bannerText}</span>
              </div>
            </div>
          </aside>
        )}

        <main className={`${styles.content} page-enter`}>
          <RouteErrorBoundary>
            <Suspense fallback={<Loader text="Loading page…" />}>
              <RouteGuard>
                <Outlet />
              </RouteGuard>
            </Suspense>
          </RouteErrorBoundary>
        </main>
      </div>

      {/* Mobile Bottom Tab Bar for student & parent (<768px) */}
      <BottomTabBar />
    </div>
  );
}
